import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useOutletContext } from "react-router-dom";
import { RiderProfileLayoutContextInterface } from "./RiderProfileLayout";
import {
    patrollerService,
    AIAnalysisResult,
    AIQuestionerItem,
    AIQuestionOption,
} from "../../Api/Services/PatrollerService";
import { riderService } from "../../Api/Services/RiderService";
import { useLoading } from "../../Context/LoadingProvider";
import { useInfoModal } from "../../Context/InfoModalProvider";
import { useUser } from "../../Context/UserProvider";
import { RiderProductModel } from "../../Api/Models/rider.model";
import { KitModel, KitProductModel } from "../../Api/Models/product.model";
import { ReturnStatus } from "../../Api/Models/return.model";
import ImageInput from "../../Components/ImageInput";
import RadioBtn from "../../Components/RadioBtn";
import CheckBtn from "../../Components/CheckBtn";
import { HornIcon } from "../../Components/Icons/HornIcon";

type Phase = "ready" | "analyzing" | "review";
type AnswerValue = string | string[] | null;

const GEAR_CONDITIONS = ["Good", "Damaged", "Missing", "Unbranded", "Old branding", "Dirty"];

const AI_POLL_TIMEOUT_MS = 120000;

const isAnswered = (ans: AnswerValue): boolean =>
    Array.isArray(ans) ? ans.length > 0 : ans !== null && ans !== undefined && ans !== "";

const optionTargets = (question: AIQuestionerItem, opt: AIQuestionOption): string[] => {
    const targets: string[] = [];
    (opt.skip ?? []).filter((s) => s !== "").forEach((s) => targets.push(s.toString()));
    if (opt.jump) {
        const cur = parseInt(question.order.toString());
        const jmp = parseInt(opt.jump);
        for (let i = cur + 1; i < jmp; i++) targets.push(i.toString());
    }
    return targets;
};

const computeQuestionVisibility = (
    questions: AIQuestionerItem[],
    answers: Record<number, AnswerValue>
): { skipped: Set<string>; gated: Set<string> } => {
    const skipped = new Set<string>();
    const gated = new Set<string>();

    const ordered = [...questions].sort(
        (a, b) => parseInt(a.order.toString()) - parseInt(b.order.toString())
    );

    ordered.forEach((q) => {
        const options = q.options ?? [];
        const allTargets = new Set<string>();
        options.forEach((o) => optionTargets(q, o).forEach((t) => allTargets.add(t)));
        if (allTargets.size === 0) return;

        const order = q.order.toString();
        const hidden = skipped.has(order) || gated.has(order);
        const ans = answers[q.id];

        if (hidden || !isAnswered(ans)) {
            allTargets.forEach((t) => gated.add(t));
            return;
        }

        const selectedOptions: AIQuestionOption[] = Array.isArray(ans)
            ? options.filter((o) => (ans as string[]).includes(o.name))
            : options.filter((o) => o.name === ans);
        selectedOptions.forEach((opt) =>
            optionTargets(q, opt).forEach((t) => skipped.add(t))
        );
    });

    return { skipped, gated };
};

const AICheckPage: React.FunctionComponent = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { rider, setSubTitle, setShowTabsNavbar, setShowProfileInfo } =
        useOutletContext<RiderProfileLayoutContextInterface>();
    const { startLoading, finnishLoading } = useLoading();
    const { showInfo } = useInfoModal();
    const { user } = useUser();
    const location = useLocation();
    const locationState = (location.state ?? {}) as {
        uploadToken?: string;
        photoPreviewUrl?: string;
    };

    const gearReport    = localStorage.getItem("gearReport");
    const historicalGear = localStorage.getItem("historicalGear");
    const showGearSection = gearReport !== "0";

    const [phase, setPhase] = useState<Phase>(locationState.uploadToken ? "analyzing" : "ready");
    const photoPreviewUrl: string | null = locationState.photoPreviewUrl ?? null;
    const [analysis, setAnalysis] = useState<AIAnalysisResult | null>(null);

    const [aiPollingToken, setAiPollingToken] = useState<string | null>(null);
    const [aiFailed, setAiFailed] = useState<boolean>(false);
    const firstAnalysisRef = useRef(true);
    const [coords, setCoords] = useState<{ lat: number; lng: number } | null>(null);

    const [needsIdCheck,        setNeedsIdCheck]        = useState<number>(0);
    const [needsStickerCheck,   setNeedsStickerCheck]   = useState<number>(0);
    const [needsVehicleCheck,   setNeedsVehicleCheck]   = useState<number>(0);
    const [stickerEditable,     setStickerEditable]     = useState<boolean>(false);

    const [fakeId,  setFakeId]  = useState<boolean | null>(null);
    const [idImage, setIdImage] = useState<File[]>([]);

    const [isStickerValid,      setIsStickerValid]      = useState<boolean | null>(null);
    const [isStickerMissing,    setIsStickerMissing]    = useState<boolean>(false);
    const [stickerImage,        setStickerImage]        = useState<File[]>([]);
    const [correctStickerImage, setCorrectStickerImage] = useState<File[]>([]);
    const [stickerNumber,       setStickerNumber]       = useState<string>("");
    const showStickerEdit = false;

    const [isLicensePlateValid,      setIsLicensePlateValid]      = useState<boolean | null>(null);
    const [licensePlate,             setLicensePlate]             = useState<string>("");
    const [licensePlateImage,        setLicensePlateImage]        = useState<File[]>([]);
    const [correctLicensePlateImage, setCorrectLicensePlateImage] = useState<File[]>([]);
    const [showLicensePlateEdit,     setShowLicensePlateEdit]     = useState<boolean>(false);

    const [gears,                    setGears]                    = useState<RiderProductModel[]>([]);
    const [gearsNotTalabat,          setGearsNotTalabat]          = useState<any[]>([]);
    const [missingKitProducts,       setMissingKitProducts]       = useState<KitModel | null>(null);
    const [gearsCondition,           setGearsCondition]           = useState<Record<string, any>>({});
    const [gearsNotTalabatCondition, setGearsNotTalabatCondition] = useState<Record<string, any>>({});
    const [gearsOverdueCommunicated, setGearsOverdueCommunicated] = useState<boolean | undefined>(false);
    const [riderHasNoUniform,        setRiderHasNoUniform]        = useState<boolean>(false);


    const [answers,           setAnswers]           = useState<Record<number, AnswerValue>>({});
    const [questionerImages,  setQuestionerImages]  = useState<Record<number, File[]>>({});
    const [multiExpanded,     setMultiExpanded]     = useState<Record<number, boolean>>({});
    const [expandedGearRow,   setExpandedGearRow]   = useState<string | null>(null);

    useEffect(() => {
        if (!expandedGearRow) return;
        const closeOnOutside = (e: Event) => {
            const target = e.target as HTMLElement | null;
            if (target?.closest("[data-gear-dropdown]")) return;
            setExpandedGearRow(null);
        };
        document.addEventListener("mousedown", closeOnOutside);
        document.addEventListener("touchstart", closeOnOutside);
        return () => {
            document.removeEventListener("mousedown", closeOnOutside);
            document.removeEventListener("touchstart", closeOnOutside);
        };
    }, [expandedGearRow]);

    useEffect(() => {
        setSubTitle(t("AI Check"));
        setShowTabsNavbar(false);
        setShowProfileInfo(true);
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (pos) => setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
                () => {}
            );
        }
        startLoading("ai-check-init");
        patrollerService
            .isRiderReadyForCheck(rider.riderId)
            .then(() => {
                const token = locationState.uploadToken;
                if (token) {
                    setPhase("analyzing");
                    startLoading("ai-start-check");
                    patrollerService
                        .startAiCheck(rider.riderId, token)
                        .then((result) => {
                            setAnalysis(result);
                            setPhase("review");
                            if (!result.aiReady) {
                                setAiPollingToken(token);
                            }
                        })
                        .catch(() => navigate("/ai-check-start", { replace: true }))
                        .finally(() => finnishLoading("ai-start-check"));
                }
            })
            .catch(() => navigate(`/rider/${rider.riderId}/profile`))
            .finally(() => finnishLoading("ai-check-init"));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!aiPollingToken) return;

        const deadline = Date.now() + AI_POLL_TIMEOUT_MS;

        const interval = setInterval(async () => {
            if (Date.now() > deadline) {
                setAiPollingToken(null);
                setAiFailed(true);
                return;
            }
            try {
                const status = await patrollerService.pollAiPhotoStatus(aiPollingToken);
                if (status.status === "ready") {
                    setAiPollingToken(null);
                    setAiFailed(false);
                    setAnalysis((prev) =>
                        prev
                            ? {
                                  ...prev,
                                  questioner: (status.questioner as any[]) ?? prev.questioner,
                                  gear: (status.gear as any[]) ?? prev.gear,
                              }
                            : prev
                    );
                } else if (status.status === "failed") {
                    setAiPollingToken(null);
                    setAiFailed(true);
                }
            } catch (_) {}
        }, 2000);
        return () => clearInterval(interval);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [aiPollingToken]);

    useEffect(() => {
        if (!analysis) return;

        setNeedsIdCheck(analysis.needsIdCheck);
        setNeedsStickerCheck(analysis.needsStickerCheck);
        setNeedsVehicleCheck(analysis.needsVehicleCheck);
        setStickerEditable(analysis.stickerEditable);

        if (analysis.needsIdCheck <= 0) setFakeId(false);

        patrollerService.setCheckForRider(rider.riderId, analysis.checkId);

        if (firstAnalysisRef.current) {
            firstAnalysisRef.current = false;

            const initial: Record<number, AnswerValue> = {};
            analysis.questioner.forEach((q) => {
                if (q.aiSuggestedAnswer) {
                    initial[q.id] =
                        q.type === "MULTIPLE_CHOICE:MULTI"
                            ? [q.aiSuggestedAnswer.name]
                            : q.aiSuggestedAnswer.name;
                } else {
                    initial[q.id] = q.type === "MULTIPLE_CHOICE:MULTI" ? [] : null;
                }
            });
            const initialSkipped = computeQuestionVisibility(analysis.questioner, initial).skipped;
            analysis.questioner.forEach((q) => {
                if (initialSkipped.has(q.order.toString())) {
                    initial[q.id] = q.type === "MULTIPLE_CHOICE:MULTI" ? [] : null;
                }
            });
            setAnswers(initial);

            if (showGearSection) fetchGearData();
        } else {
            setAnswers((prev) => {
                const next = { ...prev };
                analysis.questioner.forEach((q) => {
                    if (q.aiSuggestedAnswer && (prev[q.id] === null || prev[q.id] === undefined)) {
                        next[q.id] =
                            q.type === "MULTIPLE_CHOICE:MULTI"
                                ? [q.aiSuggestedAnswer.name]
                                : q.aiSuggestedAnswer.name;
                    }
                });
                return next;
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [analysis]);

    useEffect(() => {
        setGearsCondition((prev) => {
            const cond: Record<string, any> = {};
            gears.forEach((g, i) => {
                const key = i.toString();
                const aiCondition =
                    (analysis?.gear as any[] | undefined)?.find(
                        (ag: any) => ag.productId === g.productId
                    )?.condition ?? "Good";
                const existing = prev[key];
                const itemStatus =
                    existing && existing.itemStatus !== existing.aiStatus
                        ? existing.itemStatus
                        : aiCondition;
                cond[key] = {
                    itemStatus,
                    aiStatus:   aiCondition,
                    imageUrl:   g.productImage,
                    gearName:   g.productName + " " + (g.variationName ?? ""),
                    gearBaseName:       g.productName,
                    gearVariationName:  g.variationName,
                    gearId:             g.productId,
                    variationId:        g.productVariationId,
                    gearAssignedOn:     g.assignedAt,
                    eligibleForExchange: g.exchangeAvailableAt,
                };
            });
            return cond;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [gears, analysis?.gear]);

    useEffect(() => {
        const cond: Record<string, any> = {};
        gearsNotTalabat.forEach((g, i) => {
            cond[i.toString()] = {
                itemStatus: "Good",
                aiStatus:   "Good",
                imageUrl:   g.imageUrl,
                gearName:   g.name,
                gearId:     g.id,
                variationId: undefined,
                gearAssignedOn: undefined,
                eligibleForExchange: undefined,
            };
        });
        setGearsNotTalabatCondition(cond);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [gearsNotTalabat]);

    const fetchGearData = () => {
        startLoading("ai-gear-load");
        Promise.all([
            riderService.listGear(rider.riderId),
            patrollerService.getNonTalabatGears(),
            riderService.getMissingKitProducts(rider.riderId),
        ])
            .then(([gearList, nonTalabat, kit]) => {
                const filtered = gearList
                    .filter((p) => p.shouldBeChecked)
                    .filter((p, idx, arr) =>
                        arr.findIndex(
                            (t) =>
                                t.productId === p.productId &&
                                t.productVariationId === p.productVariationId
                        ) === idx
                    );
                setGears(filtered);
                setGearsNotTalabat(nonTalabat);
                setMissingKitProducts(kit);
            })
            .finally(() => finnishLoading("ai-gear-load"));
    };

    const riderAppearanceIsGood = useMemo(() => {
        return (
            Object.values(gearsCondition).every((g: any) => g.itemStatus === "Good") &&
            Object.values(gearsNotTalabatCondition).every((g: any) => g.itemStatus === "Good")
        );
    }, [gearsCondition, gearsNotTalabatCondition]);

    const showProvidedByTalabat = !user?.disable_gear_section;
    const overdueGears = gears.filter(
        (p) => !(p.exchangeDays && p.returnStatus !== ReturnStatus.MISSING)
    );
    const showOverdueOrMissing =
        !user?.disable_exchange_section &&
        (overdueGears.length > 0 || (missingKitProducts?.products?.length ?? 0) > 0);
    const shouldCheckCommunication =
        overdueGears.length > 0 || (missingKitProducts?.products?.length ?? 0) > 0;
    const communicationChecked =
        !shouldCheckCommunication || gearsOverdueCommunicated === true;

    const questionVisibility = useMemo(() => {
        if (!analysis) return { skipped: new Set<string>(), gated: new Set<string>() };
        return computeQuestionVisibility(analysis.questioner, answers);
    }, [analysis, answers]);

    const visibleQuestions = useMemo(() => {
        if (!analysis) return [];
        return analysis.questioner.filter((q) => {
            const order = q.order.toString();
            return !questionVisibility.skipped.has(order) && !questionVisibility.gated.has(order);
        });
    }, [analysis, questionVisibility]);

    const questionerOverrides = useMemo(() => {
        if (!analysis) return 0;
        return visibleQuestions.filter((q) => {
            if (!q.aiSuggestedAnswer) return false;
            const ans = answers[q.id];
            if (q.type === "MULTIPLE_CHOICE:MULTI") {
                const aiAns = [q.aiSuggestedAnswer.name];
                const cur = Array.isArray(ans) ? (ans as string[]) : [];
                return JSON.stringify([...aiAns].sort()) !== JSON.stringify([...cur].sort());
            }
            return ans !== q.aiSuggestedAnswer.name;
        }).length;
    }, [analysis, visibleQuestions, answers]);

    const gearOverrides = useMemo(() => {
        const talabat = Object.values(gearsCondition).filter((g: any) => g.itemStatus !== g.aiStatus).length;
        const notTalabat = Object.values(gearsNotTalabatCondition).filter((g: any) => g.itemStatus !== g.aiStatus).length;
        return talabat + notTalabat;
    }, [gearsCondition, gearsNotTalabatCondition]);

    const overrideCount = useMemo(
        () => gearOverrides + questionerOverrides,
        [gearOverrides, questionerOverrides]
    );

    const allGearsSelected = useMemo(() => {
        if (!showGearSection) return true;
        return (
            Object.values(gearsCondition).every(
                (g: any) => g.itemStatus !== undefined && g.itemStatus !== null
            ) &&
            Object.values(gearsNotTalabatCondition).every(
                (g: any) => g.itemStatus !== undefined && g.itemStatus !== null
            )
        );
    }, [gearsCondition, gearsNotTalabatCondition, showGearSection]);

    const allQuestionsAnswered = useMemo(() => {
        return visibleQuestions.every((q) => {
            if (!q.mandatory) return true;
            const ans = answers[q.id];
            if (q.type === "FILE_UPLOAD")
                return questionerImages[q.id]?.length > 0;
            if (q.type === "MULTIPLE_CHOICE:MULTI")
                return Array.isArray(ans) && (ans as string[]).length > 0;
            if (q.type === "TEXT" || q.type === "SHORT_TEXT")
                return ans !== null && ans !== "";
            if (q.type === "NUMBER")
                return ans !== null && ans !== "" && !isNaN(Number(ans));
            return ans !== null;
        });
    }, [visibleQuestions, answers, questionerImages]);

    const canSubmit =
        (needsIdCheck <= 0 || (fakeId !== null && (fakeId !== true || idImage.length > 0))) &&
        (needsStickerCheck <= 0 || isStickerValid !== null || isStickerMissing) &&
        (needsVehicleCheck <= 0 || isLicensePlateValid !== null) &&
        allGearsSelected &&
        (!showGearSection || !showOverdueOrMissing || !user?.disable_exchange_section || communicationChecked) &&
        allQuestionsAnswered;

    const sectionNums = useMemo(() => {
        let n = 1;
        const photo    = n++;
        const id       = needsIdCheck > 0      ? n++ : null;
        const sticker  = needsStickerCheck > 0  ? n++ : null;
        const vehicle  = needsVehicleCheck > 0  ? n++ : null;
        const gear     = showGearSection        ? n++ : null;
        const questioner = visibleQuestions.length > 0 ? n++ : null;
        return { photo, id, sticker, vehicle, gear, questioner };
    }, [needsIdCheck, needsStickerCheck, needsVehicleCheck, showGearSection, visibleQuestions.length]);

    const renderSectionHeader = (num: number, title: string | null, badge?: string | null) => (
        <div className="ai-check-section-header">
            <div className="ai-check-section-header__number">{num}</div>
            <div className="ai-check-section-header__title">{title}</div>
            {badge != null && (
                <div className="ai-check-section-header__badge">{badge}</div>
            )}
        </div>
    );

    const getLabel = (option: AIQuestionOption) => {
        const lang = localStorage.getItem("language") ?? "en";
        return lang === "ar" ? option.name_ar || option.name : option.name;
    };
    const getQuestion = (q: AIQuestionerItem) => {
        const lang = localStorage.getItem("language") ?? "en";
        return lang === "ar" ? q.question_ar || q.question : q.question;
    };

    const handleSubmit = async () => {
        if (!canSubmit || !analysis) return;
        startLoading("ai-check-submit");
        try {
            if (needsStickerCheck > 0) {
                await patrollerService.saveStickerStepCheckForRider(
                    rider.riderId,
                    isStickerValid ?? true,
                    isStickerMissing,
                    stickerNumber || null,
                    stickerImage[0] ?? undefined,
                    correctStickerImage[0] ?? undefined
                );
            }
            if (needsVehicleCheck > 0) {
                await patrollerService.saveVehicleStepCheckForRider(
                    rider.riderId,
                    isLicensePlateValid ?? true,
                    licensePlate || null,
                    licensePlateImage[0] ?? undefined,
                    correctLicensePlateImage[0] ?? undefined
                );
            }
            if (showGearSection) {
                const gearData: any[] = [];
                Object.values(gearsCondition).forEach((g: any) => {
                    if (g.itemStatus !== "Good") gearData.push(g);
                });
                Object.values(gearsNotTalabatCondition).forEach((g: any) => {
                    if (g.itemStatus !== "Good") gearData.push(g);
                });
                const gearFullData = {
                    meta: { riderApperanceIsGood: riderAppearanceIsGood, gearsOverdueCommunicated, riderHasNoUniform },
                    data: gearData,
                    picture: null,
                };
                await patrollerService.saveGearCheckData(rider.riderId, gearFullData);
            }

            const questionerDataForSave = visibleQuestions.map((q) => {
                const ans = answers[q.id];
                let score = 0;

                if (q.type === "MULTIPLE_CHOICE:SINGLE") {
                    const option = (q.options ?? []).find((o) => o.name === ans);
                    if (option && option.score) score = parseInt(option.score ?? "0");
                } else if (q.type === "MULTIPLE_CHOICE:MULTI") {
                    const selected = Array.isArray(ans) ? (ans as string[]) : [];
                    score = (q.options ?? [])
                        .filter((o) => selected.includes(o.name))
                        .reduce((s, o) => s + parseInt(o.score || "0"), 0);
                } else {
                    score = ans !== null && ans !== "" && ans !== undefined
                        ? (q.score ?? 0) : 0;
                }

                return {
                    answer:              ans,
                    score,
                    imageUrl:            questionerImages[q.id]?.[0] ?? [],
                    questionerQuestion:  q,
                };
            });

            await patrollerService.saveQuestionerData(rider.riderId, questionerDataForSave);

            await patrollerService.submitAICheck(rider.riderId, {
                checkId:   analysis.checkId,
                fakeId:    fakeId ?? false,
                latitude:  coords?.lat ?? null,
                longitude: coords?.lng ?? null,
                idPicture: idImage[0],
            });

            finnishLoading("ai-check-submit");

            await showInfo(t("Rider check saved successfully!") as string);
            navigate(`/rider/${rider.riderId}/profile`);
        } finally {
            finnishLoading("ai-check-submit");
        }
    };

    const renderGearRow = (
        rowKey: string,
        condition: any,
        onSelect: (nextStatus: string) => void
    ) => {
        const aiStatus  = condition.aiStatus ?? "Good";
        const curStatus = condition.itemStatus ?? aiStatus;
        const isOverridden = curStatus !== aiStatus;
        const isOpen       = expandedGearRow === rowKey;

        return (
            <div key={rowKey}>
                <div className={`ai-check-gear-row${isOverridden ? " ai-check-gear-row--edited" : ""}`}>
                    <div className="item-image">
                        <img src={condition.imageUrl} alt="gear" />
                        {condition.gearVariationName && (
                            <div className="item-image__variation-name">{condition.gearVariationName}</div>
                        )}
                    </div>
                    <div className="ai-check-gear-row__info">
                        <div>
                            <div className="ai-check-gear-row__name">
                                {condition.gearName}
                            </div>
                            <div className="ai-check-gear-row__ai-text">
                                🧠 {t("AI Analysis")}: <strong>{t(aiStatus)}</strong>
                            </div>
                        </div>
                        <div data-gear-dropdown className="ai-check-dropdown">
                            <button
                                type="button"
                                aria-haspopup="listbox"
                                aria-expanded={isOpen}
                                className={`ai-check-dropdown__trigger${(isOverridden || isOpen) ? " ai-check-dropdown__trigger--active" : ""}`}
                                onClick={() => setExpandedGearRow(isOpen ? null : rowKey)}
                            >
                                {isOverridden ? t(curStatus) : t("Disagree")}
                                <span className="ai-check-dropdown__caret">{isOpen ? "▲" : "▼"}</span>
                            </button>
                            {isOpen && (
                                <div role="listbox" className="ai-check-dropdown__menu">
                                    {GEAR_CONDITIONS.map((key) => {
                                        const selected = curStatus === key;
                                        return (
                                            <button
                                                key={key}
                                                type="button"
                                                role="option"
                                                aria-selected={selected}
                                                className={`ai-check-dropdown__option${selected ? " ai-check-dropdown__option--selected" : ""}`}
                                                onClick={() => {
                                                    onSelect(key);
                                                    setExpandedGearRow(null);
                                                }}
                                            >
                                                <span>{t(key)}</span>
                                                {selected && <span>✓</span>}
                                            </button>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    useEffect(() => {
        if (phase === "ready" && !locationState.uploadToken) {
            navigate("/ai-check-start", { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [phase]);

    if (phase === "ready") return null;


    if (phase === "analyzing") {
        return (
            <div className="flex flex-col items-center mt-8 gap-4">
                <div className="ai-check-analyzing__icon">🧠</div>
                <div className="heading">{t("Analyzing photo...")}</div>
                <div className="text-light">{t("The AI is reviewing the rider's appearance")}</div>
            </div>
        );
    }


    return (
        <>
            {aiPollingToken && (
                <div className="ai-check-notice">
                    <span className="ai-check-notice__icon">🧠</span>
                    <span>{t("AI is still analyzing the photo. Suggestions will appear shortly.")}</span>
                </div>
            )}

            {aiFailed && (
                <div className="ai-check-notice">
                    <span className="ai-check-notice__icon">⚠️</span>
                    <span>{t("AI suggestions are unavailable. Please fill in the check manually.")}</span>
                </div>
            )}

            {photoPreviewUrl && (
                <div className="mb-4">
                    {renderSectionHeader(sectionNums.photo, t("Rider Photo"), t("CAMERA ONLY"))}
                    <div className="ai-check-photo">
                        <img
                            src={photoPreviewUrl}
                            alt="rider"
                            className="ai-check-photo__img"
                        />
                    </div>
                    <div className="separator-line mt-4 mb-6"></div>
                </div>
            )}

            <div className="ai-check-banner">
                <div className="ai-check-banner__icon">🧠</div>
                <div>
                    <div className="ai-check-banner__title">{t("AI Analysis complete")}</div>
                    <div className="ai-check-banner__subtitle">
                        {t("Review each item · tap Disagree if you see an issue the AI missed")}
                    </div>
                </div>
            </div>

            {needsIdCheck > 0 && (
                <>
                    {renderSectionHeader(sectionNums.id!, t("ID Check"))}
                    <div className="mb-1 heading">{t("Does the rider have a fake ID?")}</div>
                    <div className="text-light mb-4">{t("Please choose an option!")}</div>
                    {rider.idCardNumber && (
                        <div className="license-plate mb-4">{rider.idCardNumber}</div>
                    )}
                    <div className="flex items-center gap-4 mb-2">
                        <RadioBtn label={t("Yes")} selected={fakeId === true}
                            onSelect={() => setFakeId(true)} size="large" />
                        <RadioBtn label={t("No")} selected={fakeId === false}
                            onSelect={() => setFakeId(false)} size="large" />
                    </div>
                    {fakeId === true && (
                        <>
                            <div className="separator-line mb-2"></div>
                            <ImageInput images={idImage} setImages={setIdImage}
                                title={t("Take a photo")} infoText={t("Confirmation photo")}
                                multiple={false} required={false} className="mb-4" />
                        </>
                    )}
                    <div className="separator-line mt-4 mb-6"></div>
                </>
            )}

            {needsStickerCheck > 0 && (
                <>
                    {!showStickerEdit ? (
                        <>
                            {renderSectionHeader(sectionNums.sticker!, t("Sticker Check"))}
                            <div className="mb-1 heading">{t("Confirm the rider's sticker?")}</div>
                            <div className="license-plate mt-4 mb-4">
                                {rider.sticker ?? t("No sticker")}
                            </div>
                            <div className="flex items-center gap-4 mb-2">
                                <RadioBtn label={t("Yes")} selected={isStickerValid === true && !isStickerMissing}
                                    onSelect={() => { setIsStickerValid(true); setIsStickerMissing(false); }} size="large" />
                                <RadioBtn label={t("No")} selected={isStickerValid === false && !isStickerMissing}
                                    onSelect={() => { setIsStickerValid(false); setIsStickerMissing(false); }} size="large" />
                                <RadioBtn label={t("Missing")} selected={isStickerMissing}
                                    onSelect={() => { setIsStickerMissing(true); setIsStickerValid(false); }} size="large" />
                            </div>
                            {isStickerValid === false && !isStickerMissing && stickerEditable && (
                                <div className="mb-4">
                                    <input className="input-field w-full" type="text"
                                        placeholder={t("Sticker number") || ""}
                                        value={stickerNumber}
                                        onChange={(e) => setStickerNumber(e.target.value)} />
                                </div>
                            )}
                            {(isStickerValid === false || isStickerMissing) && (
                                <>
                                    <div className="separator-line mb-2"></div>
                                    <ImageInput images={stickerImage} setImages={setStickerImage}
                                        title={t("Take a photo")} infoText={t("Confirmation photo")}
                                        multiple={false} required={false} className="mb-4" />
                                </>
                            )}
                        </>
                    ) : (
                        <>
                            <div className="mb-1 heading">{t("What's the Rider sticker?")}</div>
                            <div className="text-light mb-4">{t("Please fill the correct sticker!")}</div>
                            <div className="mb-4">
                                <input className="input-field w-full" type="text"
                                    placeholder={t("Sticker") || ""}
                                    value={stickerNumber}
                                    onChange={(e) => setStickerNumber(e.target.value)} />
                            </div>
                            <ImageInput images={correctStickerImage} setImages={setCorrectStickerImage}
                                title={t("Take a photo")} infoText={t("Confirmation photo")}
                                multiple={false} required={false} />
                        </>
                    )}
                    <div className="separator-line mt-4 mb-6"></div>
                </>
            )}

            {needsVehicleCheck > 0 && (
                <>
                    {!showLicensePlateEdit ? (
                        <>
                            {renderSectionHeader(sectionNums.vehicle!, t("Vehicle Check"))}
                            <div className="mb-1 heading">{t("Confirm the rider's vehicle plate number?")}</div>
                            <div className="license-plate mt-4 mb-4">
                                {rider.licensePlate ?? t("No license plate")}
                            </div>
                            <div className="flex items-center gap-4 mb-2">
                                <RadioBtn label={t("Yes")} selected={isLicensePlateValid === true}
                                    onSelect={() => { setIsLicensePlateValid(true); setShowLicensePlateEdit(false); }} size="large" />
                                <RadioBtn label={t("No")} selected={isLicensePlateValid === false}
                                    onSelect={() => { setIsLicensePlateValid(false); setShowLicensePlateEdit(true); }} size="large" />
                            </div>
                            {isLicensePlateValid === false && (
                                <>
                                    <div className="separator-line mb-2"></div>
                                    <ImageInput images={licensePlateImage} setImages={setLicensePlateImage}
                                        title={t("Take a photo")} infoText={t("Confirmation photo")}
                                        multiple={false} required={false} className="mb-4" />
                                </>
                            )}
                        </>
                    ) : (
                        <>
                            <div className="mb-1 heading">{t("What's the Rider vehicle plate number?")}</div>
                            <div className="text-light mb-4">{t("Please fill the correct license plate!")}</div>
                            <div className="mb-4">
                                <input className="input-field w-full" type="text"
                                    placeholder={t("License plate number") || ""}
                                    value={licensePlate}
                                    onChange={(e) => setLicensePlate(e.target.value)} />
                            </div>
                            <ImageInput images={correctLicensePlateImage} setImages={setCorrectLicensePlateImage}
                                title={t("Take a photo")} infoText={t("Confirmation photo")}
                                multiple={false} required={false} />
                        </>
                    )}
                    <div className="separator-line mt-4 mb-6"></div>
                </>
            )}

            {showGearSection && (
                <>
                    {renderSectionHeader(sectionNums.gear!, t("Gear Check"), t("AI-AUTOMATED"))}
                    {historicalGear === "1" && Object.keys(gearsCondition).length === 0 && (
                        <div className="flex gap-4 mb-2">
                            <div className="flex-1">
                                <button type="button" className="button"
                                    onClick={() => setRiderHasNoUniform(true)}>
                                    {riderHasNoUniform ? t("Uniform not provided") : t("Rider has no uniform")}
                                </button>
                            </div>
                        </div>
                    )}

                    <div className="separator-line mb-2"></div>

                    {showProvidedByTalabat && Object.keys(gearsCondition).length > 0 && (
                        <>
                            <div className="heading mb-2">{t("Gear Provided by Talabat")}</div>
                            <div className="text-light mb-4">
                                {t("AI has pre-filled each item — tap Disagree if incorrect")}
                            </div>
                            <div className="mb-4">
                                {Object.keys(gearsCondition).map((idx) =>
                                    renderGearRow(
                                        `t-${idx}`,
                                        gearsCondition[idx],
                                        (nextStatus) => setGearsCondition((p) => ({ ...p, [idx]: { ...p[idx], itemStatus: nextStatus } }))
                                    )
                                )}
                            </div>
                        </>
                    )}

                    {showOverdueOrMissing && (
                        <>
                            <div className="heading mb-4">{t("Gear Overdue or Not Received")}</div>
                            <div className="card flex flex-col gap-2 mb-4">
                                {overdueGears.map((product, idx) => (
                                    <div key={idx} className="flex gap-2">
                                        <div className="item-image">
                                            <img src={product.productImage} alt="gear" />
                                            {product.variationName && (
                                                <div className="item-image__variation-name">{product.variationName}</div>
                                            )}
                                        </div>
                                        <div className="combined-btn">
                                            <div>
                                                <div className="combined-btn__label">{product.productName}</div>
                                                <div className="combined-btn__sublabel">
                                                    {t("Overdue since:")} {product.exchangeAvailableAtConst}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                                {missingKitProducts &&
                                    Object.entries(
                                        missingKitProducts.products.reduce((groups, product) => {
                                            const groupId = product.optionalGroupId ?? "ungrouped";
                                            if (!groups[groupId]) groups[groupId] = [];
                                            groups[groupId].push(product);
                                            return groups;
                                        }, {} as Record<string | number, KitProductModel[]>)
                                    ).map(([groupId, groupProducts], gIdx) => {
                                        if (groupId === "ungrouped") {
                                            return groupProducts.map((p, i) => (
                                                <div key={`ug-${i}`} className="flex gap-2">
                                                    <div className="item-image"><img src={p.image} alt="gear" /></div>
                                                    <div className="combined-btn">
                                                        <div className="combined-btn__label">{p.name}</div>
                                                        <div className="combined-btn__sublabel">{t("Not received")}</div>
                                                    </div>
                                                </div>
                                            ));
                                        }
                                        const names = groupProducts.map((p, i) => (
                                            <React.Fragment key={i}>
                                                {p.name}
                                                {i < groupProducts.length - 1 && <> <strong>{t("or")}</strong> </>}
                                            </React.Fragment>
                                        ));
                                        return (
                                            <div key={`g-${gIdx}`} className="flex gap-2">
                                                <div className="item-image"><img src={groupProducts[0].image} alt="gear" /></div>
                                                <div className="combined-btn">
                                                    <div className="combined-btn__label">{names}</div>
                                                    <div className="combined-btn__sublabel">{t("Not received")}</div>
                                                </div>
                                            </div>
                                        );
                                    })}
                            </div>
                            <CheckBtn
                                label={t("Communicated to the rider!")}
                                labelIcon={<HornIcon />}
                                selected={gearsOverdueCommunicated === true}
                                onChange={(checked: boolean) => setGearsOverdueCommunicated(checked)}
                                className="mb-4 check-btn--purple-text check-btn--medium-text"
                                reversed={true}
                            />
                        </>
                    )}

                    {Object.keys(gearsNotTalabatCondition).length > 0 && (
                        <>
                            <div className="heading mb-2">{t("Gear Not Provided by Talabat")}</div>
                            <div className="text-light mb-4">
                                {t("AI has pre-filled each item — tap Disagree if incorrect")}
                            </div>
                            <div className="mb-4">
                                {Object.keys(gearsNotTalabatCondition).map((idx) =>
                                    renderGearRow(
                                        `nt-${idx}`,
                                        gearsNotTalabatCondition[idx],
                                        (nextStatus) => setGearsNotTalabatCondition((p) => ({ ...p, [idx]: { ...p[idx], itemStatus: nextStatus } }))
                                    )
                                )}
                            </div>
                        </>
                    )}

                    <div className="separator-line mt-4 mb-6"></div>
                </>
            )}

            {visibleQuestions.length > 0 && sectionNums.questioner !== null &&
                renderSectionHeader(sectionNums.questioner, t("Questioner"), t("AI + MANUAL"))
            }
            {visibleQuestions.map((q) => {
                const isAiAssessed = !!q.aiSuggestedAnswer;
                const curAns = answers[q.id];

                const aiLabel = q.aiSuggestedAnswer
                    ? (q.type === "YES_NO"
                        ? (q.aiSuggestedAnswer.name === "YES" ? t("Yes") : t("No"))
                        : getLabel(q.aiSuggestedAnswer))
                    : null;

                const isOverridden = isAiAssessed && curAns !== q.aiSuggestedAnswer?.name;
                const isExpanded   = !!multiExpanded[q.id];

                const singleOpts = q.type === "YES_NO"
                    ? [{ name: "YES", label: t("Yes") }, { name: "NO", label: t("No") }]
                    : q.options.map((o) => ({ name: o.name, label: getLabel(o) }));

                return (
                    <div key={q.id} className="mb-6">
                        {isAiAssessed
                            ? <div className="ai-check-tag ai-check-tag--ai">🧠 {t("AI ASSESSED")}</div>
                            : <div className="ai-check-tag ai-check-tag--manual">✋ {t("MANUAL")}</div>
                        }

                        <p className="heading mb-3">{getQuestion(q)}</p>

                        {q.imageUrl !== "" && (
                            <div className="flex justify-center mb-4">
                                <img src={q.imageUrl} alt="" />
                            </div>
                        )}

                        {(q.type === "YES_NO" || q.type === "MULTIPLE_CHOICE:SINGLE") && isAiAssessed && (
                            <>
                                <div className="ai-check-ai-row">
                                    <span className="ai-check-ai-row__text">
                                        {t("AI Analysis")}: <strong>{aiLabel}</strong>
                                    </span>
                                    <button
                                        type="button"
                                        className={`ai-check-disagree-btn${(isOverridden || isExpanded) ? " ai-check-disagree-btn--active" : ""}`}
                                        onClick={() => setMultiExpanded((p) => ({ ...p, [q.id]: !p[q.id] }))}
                                    >
                                        {isOverridden
                                            ? singleOpts.find((o) => o.name === curAns)?.label ?? t("Disagree")
                                            : t("Disagree")
                                        }
                                    </button>
                                </div>
                                {isExpanded && (
                                    <div className="ai-check-option-list">
                                        {singleOpts.map((opt) => {
                                            const selected = curAns === opt.name;
                                            return (
                                                <button key={opt.name} type="button"
                                                    className={`ai-check-option-btn${selected ? " ai-check-option-btn--selected" : ""}${singleOpts.length === 2 ? " ai-check-option-btn--grow" : ""}`}
                                                    onClick={() => {
                                                        setAnswers((p) => ({ ...p, [q.id]: opt.name }));
                                                        setMultiExpanded((p) => ({ ...p, [q.id]: false }));
                                                    }}
                                                >
                                                    <span className={`ai-check-option-dot${selected ? " ai-check-option-dot--selected" : ""}`} />
                                                    {opt.label}
                                                </button>
                                            );
                                        })}
                                    </div>
                                )}
                            </>
                        )}

                        {(q.type === "YES_NO" || q.type === "MULTIPLE_CHOICE:SINGLE") && !isAiAssessed && (
                            <div className="ai-check-option-list">
                                {singleOpts.map((opt) => {
                                    const selected = curAns === opt.name;
                                    return (
                                        <button key={opt.name} type="button"
                                            className={`ai-check-option-btn${selected ? " ai-check-option-btn--selected" : ""}${singleOpts.length === 2 ? " ai-check-option-btn--grow" : ""}`}
                                            onClick={() => setAnswers((p) => ({ ...p, [q.id]: opt.name }))}
                                        >
                                            <span className={`ai-check-option-dot${selected ? " ai-check-option-dot--selected" : ""}`} />
                                            {opt.label}
                                        </button>
                                    );
                                })}
                            </div>
                        )}

                        {q.type === "MULTIPLE_CHOICE:MULTI" && isAiAssessed && (
                            <div className="ai-check-ai-row">
                                <span className="ai-check-ai-row__text">
                                    {t("AI Analysis")}: <strong>{aiLabel}</strong>
                                </span>
                                <button
                                    type="button"
                                    className={`ai-check-disagree-btn${multiExpanded[q.id] ? " ai-check-disagree-btn--active" : ""}`}
                                    onClick={() => setMultiExpanded((p) => ({ ...p, [q.id]: !p[q.id] }))}
                                >
                                    {t("Disagree")}
                                </button>
                            </div>
                        )}
                        {q.type === "MULTIPLE_CHOICE:MULTI" && (!isAiAssessed || multiExpanded[q.id]) && q.options.map((opt) => {
                            const sel = Array.isArray(curAns) ? (curAns as string[]) : [];
                            return (
                                <CheckBtn key={opt.name} label={getLabel(opt)} selected={sel.includes(opt.name)}
                                    onChange={() => {
                                        const cur = Array.isArray(curAns) ? (curAns as string[]) : [];
                                        const next = cur.includes(opt.name)
                                            ? cur.filter((x) => x !== opt.name)
                                            : [...cur, opt.name];
                                        setAnswers((p) => ({ ...p, [q.id]: next }));
                                    }} className="mb-4" />
                            );
                        })}

                        {q.type === "TEXT" && (
                            <textarea className="input-field"
                                value={(curAns as string) ?? ""}
                                onChange={(e) => setAnswers((p) => ({ ...p, [q.id]: e.target.value }))} />
                        )}
                        {q.type === "SHORT_TEXT" && (
                            <input className="input-field mb-4"
                                value={(curAns as string) ?? ""}
                                onChange={(e) => setAnswers((p) => ({ ...p, [q.id]: e.target.value }))} />
                        )}
                        {q.type === "NUMBER" && (
                            <input className="input-field" type="number" inputMode="numeric" pattern="[0-9]*"
                                value={(curAns as string) ?? ""}
                                onChange={(e) => {
                                    const val = e.target.value.replace(/\D/g, "");
                                    setAnswers((p) => ({ ...p, [q.id]: val }));
                                }} />
                        )}

                        {q.type === "FILE_UPLOAD" && (
                            <ImageInput
                                images={questionerImages[q.id] ?? []}
                                setImages={(filesOrUpdater: File[] | ((prev: File[]) => File[])) =>
                                    setQuestionerImages((p) => {
                                        const prev = p[q.id] ?? [];
                                        const next = typeof filesOrUpdater === "function"
                                            ? filesOrUpdater(prev)
                                            : filesOrUpdater;
                                        return { ...p, [q.id]: next };
                                    })
                                }
                                title={t("Take a photo")}
                                infoText={t("Confirmation Photo")}
                                multiple={false}
                                required={!!q.mandatory}
                            />
                        )}
                    </div>
                );
            })}

            <div className="ai-check-footer">
                <div className="ai-check-footer__status">
                    {t("REVIEW COMPLETE")} · {overrideCount} {t(overrideCount === 1 ? "OVERRIDE LOGGED" : "OVERRIDES LOGGED")}
                </div>
                <button
                    className="button w-full"
                    disabled={!canSubmit}
                    onClick={handleSubmit}
                >
                    {t("Submit Check")}
                </button>
            </div>
        </>
    );
};

export default AICheckPage;
